<template>
  <div>
    <transition name="fade">
      <v-overlay :z-index="202 + layer * 2 - 1" v-if="value"></v-overlay>
    </transition>
    <v-dialog persistent :value="value" hide-overlay :max-width="maxWidth">
      <v-card class="unmarkable">
        <v-card-title v-if="dialogTitle">
          <v-icon v-if="!!icon">{{ icon }}</v-icon>
          &nbsp;
          {{ dialogTitle }}
        </v-card-title>
        <v-divider v-if="dialogTitle" />
        <v-card-text v-if="content.length > 0">
          <template v-for="c in content">
            <p v-if="c.type === 'text'" :key="c.id">{{ c.initialValue }}</p>
            <v-text-field
              v-if="c.type === 'textInput'"
              :key="c.id"
              :ref="c.id"
              :placeholder="c.placeholder"
              :value="result[c.id]"
              :label="c.inputLabel"
              :error-messages="errorMessages[c.id]"
              @input="value => onInput(value, c.id)"
              :hint="hint[c.id]"
              :persistent-hint="true"
            >
              <template v-if="c.append" v-slot:append>{{ c.append }}</template>
            </v-text-field>
            <v-text-field
              v-if="c.type === 'numberInput'"
              type="number"
              :key="c.id"
              :ref="c.id"
              :placeholder="c.placeholder"
              :value="result[c.id]"
              :label="c.inputLabel"
              :error-messages="errorMessages[c.id]"
              @input="value => onInput(value, c.id)"
              :hint="hint[c.id]"
              :persistent-hint="true"
            ></v-text-field>
            <v-select
              v-if="c.type === 'dropdown'"
              :key="c.id"
              :items="c.dropdownSelection"
              :placeholder="c.placeholder"
              :value="result[c.id]"
              :label="c.inputLabel"
              :error-messages="errorMessages[c.id]"
              @input="value => onInput(value, c.id)"
              :hint="hint[c.id]"
              :persistent-hint="true"
            ></v-select>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" text @click="onCancelClick">{{
            cancelText
          }}</v-btn>
          <v-btn
            color="secondary darken-1"
            text
            @click="onOkClick"
            :disabled="hasErrorMessages"
            >{{ okText }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
const _ = require("lodash");

function removeEnding(str, ending) {
  if (_.endsWith(str, ending)) {
    return str.slice(0, -ending.length);
  } else {
    return str;
  }
}

export default {
  name: "GenericInputDialogFromAction",

  data: function() {
    return {
      value: false,
      result: {},
      hint: {},
      errorMessages: {},
      focus: false
    };
  },

  updated: function() {
    if (this.focus) {
      this.focusInput(this.focus);
    }
  },

  computed: {
    ...mapState("genericInputDialog", [
      "showDialog",
      "dialogTitle",
      "okText",
      "cancelText",
      "maxWidth",
      "onOk",
      "onCancel",
      "resolve",
      "icon",
      "content",
      "autofocus",
      "layer"
    ]),
    hasErrorMessages() {
      let hasError = false;
      _.forEach(this.errorMessages, value => {
        if (value) {
          hasError = true;
        }
      });
      return hasError;
    }
  },

  watch: {
    showDialog(newValue) {
      if (newValue === true) {
        this.value = newValue;
        _.forEach(this.content, c => {
          this.$set(
            this.result,
            c.id,
            c.append ? removeEnding(c.initialValue, c.append) : c.initialValue
          );
          if (c.createValidationErrorMessage) {
            this.$set(
              this.errorMessages,
              c.id,
              c.createValidationErrorMessage(this.result[c.id])
            );
          }
          if (c.min || c.max) {
            this.$set(
              this.errorMessages,
              c.id,
              this.createMinMaxErrorMessage(this.result[c.id], c.min, c.max)
            );
          }
          if (c.createHint) {
            this.$set(this.hint, c.id, c.createHint(this.result[c.id]));
          }
        });
        if (this.autofocus) {
          this.focus = this.autofocus;
        }
      } else {
        this.value = newValue;
        this.errorMessages = {};
        this.result = {};
        this.hint = {};
      }
    }
  },

  methods: {
    onOkClick() {
      const result = this.getResult();
      result.cancelled = false;
      this.onOk(result);
      this.resolve(result);
    },
    onCancelClick() {
      const result = this.getResult();
      result.cancelled = true;
      this.onCancel(result);
      this.resolve(result);
    },
    onInput(value, id) {
      this.result[id] = value;
      const contentToUpdate = _.find(this.content, { id: id });
      if (contentToUpdate.createValidationErrorMessage) {
        this.errorMessages[id] = contentToUpdate.createValidationErrorMessage(
          value
        );
      }
      if (contentToUpdate.type === "numberInput") {
        this.errorMessages[id] = this.createMinMaxErrorMessage(
          value,
          contentToUpdate.min,
          contentToUpdate.max
        );
      }
      if (contentToUpdate.createHint) {
        this.hint[id] = contentToUpdate.createHint(value);
      }
    },
    getResult() {
      const result = Object.assign({}, this.result);
      this.content.forEach(c => {
        if (c.append) {
          result[c.id] += c.append;
        }
        if (c.type === "numberInput") {
          result[c.id] = Number(result[c.id]);
        }
      });
      return result;
    },
    async focusInput(input) {
      let numberOfTries = 0;
      while (numberOfTries < 5) {
        numberOfTries++;
        if (this.focus && this.$refs[input]) {
          this.focus = false;
          this.$nextTick(() => this.$refs[this.autofocus][0].focus());
          break;
        } else {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
    },
    createMinMaxErrorMessage(value, min, max) {
      if (value.length === 0) {
        return "Please enter a number";
      }
      if (min !== undefined && min > Number(value)) {
        return `Value too small, must be greater than or equal to ${min}`;
      }
      if (max !== undefined && max < Number(value)) {
        return `Value too big, must be smaller than or equal to ${max}`;
      }
      return "";
    }
  }
};
</script>

<style scoped></style>
